import sortBy from 'lodash/sortBy';
import { useCallback, useMemo, useState } from 'react';

import {
  SimpleModalActions,
  SimpleModalContentInner,
  SimpleModalContentInset,
  SimpleModalContentOuter,
  SimpleModalCardItem,
} from 'lib/modal/simple-modal-scrollable.style';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import { Heading20 } from 'approot/shared/styles/typography.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { getSectionsAndSubSectionsOverview } from 'approot/activity-builder/activity-builder.utils';
import useGetPDFPreviews from 'approot/shared/pdf-preview/pdf-preview.hooks';
import {
  ACTIVITY_BUILDER_SLIDE_RATIO,
  SectionOrSubSectionOverview,
} from 'approot/activity-builder/activity-builder.constants';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import CheckableCard from 'approot/shared/worksheet-card/checkbox-worksheet-card/checkable-card';
import { WorksheetCardImage } from 'approot/shared/worksheet-card/worksheet-card-image';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button';
import { NegativeButton } from 'approot/shared/styles/buttons/negative-button';
import { useBookmarkAssessmentActivities } from 'approot/shared/bookmarks/use-bookmark-assessment-activities';
import {
  handleCheckboxChange,
  handleSectionIdCheckboxChange,
  pushAssignDataEvent,
  pushUnassignDataEvent,
} from './assign-modal.utils';
import AssignClassDetails from './assign-class-details';
import AssignStimulusCards from './assign-stimulus-cards';
import { useGetPublishedAssessmentActivitiesByVersion } from 'approot/activity-builder/graphql/use-get-published-assessment-activities-by-version';
import { GET_ASSESSMENT_ASSIGN_DATA } from './assign-assessment.graphql';
import type {
  GetAssessmentAssignDataQuery,
  GetAssessmentAssignDataQueryVariables,
} from './assign-assessment.graphql.generated';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { ASSESSMENT, ASSESSMENT_ACTIVITIES } from 'global/constants';
import AssignmentsFeedback from 'approot/shared/assignments-feedback/assignments-feedback';
import SimpleCardLabel from 'approot/shared/simple-card/simple-card-label';
import AssignModalWrapper from './assign-modal-wrapper';
import AssignModalNoPages from './assign-modal-no-pages';
import AssignNotificationActivities from './assign-notification-activities';
import { useGetClassStudents } from 'approot/students/use-get-class-students';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import { useGetActivityMetadata } from 'approot/activity-builder/logic/use-get-activity-details';
import AssignmentModalConfirmClose from 'approot/assignments/assignment/modals/assignment-modal-confirm-close';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  contentId: number;
  unitId: number;
  version: string;
  onClose: () => void;
  onCompleted?: (
    assignedData: Pluck<
      GetAssessmentAssignDataQuery,
      'getAssessmentById.assignedData'
    > | null,
    bookmarked: boolean
  ) => void;
};

const AssignAssessmentActivitiesModal = ({
  contentId,
  unitId,
  version,
  onClose,
  onCompleted,
}: Props) => {
  const { showSuccess } = useSnackbar();
  const currentClass = useCurrentClassVar();

  const [isUnassign, setIsUnassign] = useState<boolean>(false);
  const [isStep1, setIsStep1] = useState<boolean>(true);
  const [showCloseAssignmentModal, setShowCloseAssignmentModal] =
    useState<boolean>(false);
  const [activityPages, setActivityPages] = useState<string[]>([]);
  const [stimulusPages, setStimulusPages] = useState<number[]>([]);
  const { hasStudents, loading: studentsLoading } = useGetClassStudents();

  const classId = currentClass?.id || null;

  const { data: activitiesData, loading: activitiesLoading } =
    useGetPublishedAssessmentActivitiesByVersion({
      version,
      skip: !classId,
      classId: classId,
      isSignedIn: true,
    });

  const { data: assignData, loading: loadingAssignData } = useLoggingQuery<
    GetAssessmentAssignDataQuery,
    GetAssessmentAssignDataQueryVariables
  >(GET_ASSESSMENT_ASSIGN_DATA, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        id: contentId,
        classId: classId,
      },
    },
    onCompleted: res => {
      const metaData = res.getAssessmentById?.assignedData;
      setActivityPages(metaData?.activitiesBookmarks?.[0]?.sectionIds || []);
      setStimulusPages(metaData?.stimulusResourceIds || []);
    },
  });

  const assignedStructure = assignData?.getAssessmentById?.assignedData || null;
  const stimulusResources = useMemo(
    () => assignData?.getAssessmentById?.stimulusResources || [],
    [assignData?.getAssessmentById?.stimulusResources]
  );

  const printPdfKey =
    activitiesData?.getPublishedAssessmentActivitiesByVersion.printPdfKey;
  const overviews =
    activitiesData?.getPublishedAssessmentActivitiesByVersion.sectionsOverview;

  const hasAssignablePages = useMemo(() => {
    return (overviews && overviews.length > 0) || stimulusResources.length > 0;
  }, [overviews, stimulusResources]);

  const {
    loading: isBookmarkAssessmentResourcesLoading,
    handleBookmarkResources: handleBookmarkAssessmentResources,
  } = useBookmarkAssessmentActivities({
    assessmentId: contentId,
    unitId,
    assignedData: {
      ...assignedStructure,
      stimulusResourceIds: assignedStructure?.stimulusResourceIds || [],
      activitiesBookmarks: assignedStructure?.activitiesBookmarks?.[0],
    },
    onCompleted,
  });

  const sortedSectionsOverview = useMemo(
    () => sortBy(overviews, 'sort'),
    [overviews]
  );

  const sectionsAndSubSectionsOverview = useMemo(
    () => getSectionsAndSubSectionsOverview(sortedSectionsOverview),
    [sortedSectionsOverview]
  );

  const allSelectedValue = useMemo(
    () => ['introduction', ...sectionsAndSubSectionsOverview.map(s => s.id)],
    [sectionsAndSubSectionsOverview]
  );

  const { getMetaData } = useGetActivityMetadata({
    sectionsOverview: sortedSectionsOverview,
    contentType: ASSESSMENT_ACTIVITIES,
  });

  const { data, loading: loadingPreviews } = useGetPDFPreviews({
    documentKey: printPdfKey || '',
    previewSize: 600,
    fetchPolicy: 'cache-and-network',
  });

  const previews = data?.getPDFPreviews;

  const imageRatio = ACTIVITY_BUILDER_SLIDE_RATIO * 100;

  const isAllActivityPagesSelected = useMemo(() => {
    return allSelectedValue.length === activityPages?.length;
  }, [allSelectedValue, activityPages]);

  const isAllStimulusPagesSelected = useMemo(() => {
    return stimulusPages?.length === stimulusResources?.length;
  }, [stimulusPages, stimulusResources]);

  const handleSelectAllPages = useCallback(() => {
    setActivityPages(allSelectedValue);
  }, [allSelectedValue, setActivityPages]);

  const handleDeselectAllPages = useCallback(() => {
    setActivityPages([]);
  }, [setActivityPages]);

  const handleSelectAllStimulus = useCallback(() => {
    if (stimulusResources) {
      setStimulusPages(stimulusResources.map(s => s.id));
    }
  }, [stimulusResources, setStimulusPages]);

  const handleDeselectAllStimulus = useCallback(() => {
    setStimulusPages([]);
  }, [setStimulusPages]);

  const handleSubmit = useCallback(() => {
    handleBookmarkAssessmentResources({
      activitiesBookmarks: [
        {
          __typename: 'ActivitiesBookmarkMetaData',
          sectionIds: activityPages,
          version,
        },
      ],
      stimulusResourceIds: stimulusPages,
    }).then(() => {
      showSuccess('Work successfully assigned to your class.');
      setIsStep1(false);
      pushAssignDataEvent({
        contentId,
        contentType: ASSESSMENT,
        data: {
          activities: activityPages,
          stimulus: stimulusPages,
        },
      });
    });
  }, [
    contentId,
    version,
    stimulusPages,
    activityPages,
    setIsStep1,
    handleBookmarkAssessmentResources,
    showSuccess,
  ]);

  const getTitleDetails = useCallback(
    (section: SectionOrSubSectionOverview) => {
      return getMetaData(section);
    },
    [getMetaData]
  );

  const handleUnassign = useCallback(() => {
    setIsUnassign(true);
    handleBookmarkAssessmentResources({
      activitiesBookmarks: [],
      stimulusResourceIds: [],
    }).then(() => {
      showSuccess('Work successfully unassigned from your class.');
      pushUnassignDataEvent({
        contentId,
        contentType: ASSESSMENT,
      });
      setShowCloseAssignmentModal(false);
      onClose();
    });
  }, [
    contentId,
    handleBookmarkAssessmentResources,
    setIsUnassign,
    onClose,
    showSuccess,
  ]);

  if (showCloseAssignmentModal) {
    return (
      <AssignmentModalConfirmClose
        isLoading={isUnassign && isBookmarkAssessmentResourcesLoading}
        handleClose={() => setShowCloseAssignmentModal(false)}
        handleConfirm={handleUnassign}
      />
    );
  }

  return (
    <AssignModalWrapper isStep1={isStep1} onClose={onClose}>
      {isStep1 ? (
        <SimpleModalContentOuter>
          <SimpleModalContentInner>
            <SimpleModalContentInset>
              {loadingPreviews ||
              activitiesLoading ||
              loadingAssignData ||
              studentsLoading ? (
                <Bouncer />
              ) : !hasAssignablePages ? (
                <AssignModalNoPages />
              ) : (
                <>
                  <AssignNotificationActivities hasStudents={hasStudents} />

                  <Box
                    $alignItems="center"
                    $justifyContent="space-between"
                    $marginBottom={16}
                  >
                    <Heading20 marginBottom={0}>Activities</Heading20>
                    <SecondaryButton
                      type="button"
                      aria-label={`${
                        isAllActivityPagesSelected ? 'Deselect' : 'Select'
                      } all Activity pages`}
                      onClick={() => {
                        if (isAllActivityPagesSelected) {
                          handleDeselectAllPages();
                        } else {
                          handleSelectAllPages();
                        }
                      }}
                      data-selector={
                        isAllActivityPagesSelected
                          ? 'btn-deselect-activities'
                          : 'btn-select-activities'
                      }
                    >
                      {isAllActivityPagesSelected
                        ? 'Deselect All Activities'
                        : 'Select All Activities'}
                    </SecondaryButton>
                  </Box>
                  <BoxList $gap={16} data-selector="modal-section-activities">
                    {previews?.map((page, index) => {
                      const details =
                        index > 0
                          ? getTitleDetails(
                              sectionsAndSubSectionsOverview[index - 1]
                            )
                          : null;
                      const sectionId =
                        index === 0
                          ? 'introduction'
                          : sectionsAndSubSectionsOverview[index - 1].id;
                      return (
                        <SimpleModalCardItem
                          key={sectionId}
                          data-selector="content-card-box"
                        >
                          <CheckableCard
                            content={
                              <WorksheetCardImage
                                imageUrl={page?.urls?.[0]?.url || ''}
                                ratio={imageRatio}
                              />
                            }
                            title={
                              index === 0 ? (
                                'Introduction'
                              ) : (
                                <SimpleCardLabel
                                  title={details?.title || ''}
                                  description={details?.description || ''}
                                  differentiation={
                                    details?.differentiation ||
                                    Differentiation.GreenCircle
                                  }
                                />
                              )
                            }
                            ratio={imageRatio}
                            fieldName="pages"
                            value={sectionId}
                            id={sectionId}
                            checked={activityPages.includes(sectionId)}
                            onChange={value =>
                              setActivityPages(
                                handleSectionIdCheckboxChange(
                                  value,
                                  activityPages
                                )
                              )
                            }
                          />
                        </SimpleModalCardItem>
                      );
                    })}
                  </BoxList>
                  <AssignStimulusCards
                    stimulusResources={stimulusResources || []}
                    stimulusPages={stimulusPages}
                    isAllSelected={isAllStimulusPagesSelected}
                    onToggleAll={() => {
                      if (isAllStimulusPagesSelected) {
                        handleDeselectAllStimulus();
                      } else {
                        handleSelectAllStimulus();
                      }
                    }}
                    onChange={value =>
                      setStimulusPages(
                        handleCheckboxChange(value, stimulusPages)
                      )
                    }
                  />
                </>
              )}
            </SimpleModalContentInset>
          </SimpleModalContentInner>
          {!loadingPreviews && !activitiesLoading && !loadingAssignData && (
            <SimpleModalActions>
              <Box $gap={16} $alignItems="center">
                {assignedStructure &&
                  assignData?.getAssessmentById?.isAssigned && (
                    <NegativeButton
                      type="button"
                      variant="outlined"
                      disabled={
                        loadingPreviews || isBookmarkAssessmentResourcesLoading
                      }
                      onClick={() => setShowCloseAssignmentModal(true)}
                      data-selector="btn-unassign-all"
                    >
                      Close Assignment
                    </NegativeButton>
                  )}

                <AssignmentsFeedback buttonText="Feedback about Assignments?" />
              </Box>
              <Box $gap={16}>
                <SecondaryButton
                  type="button"
                  disabled={
                    loadingPreviews || isBookmarkAssessmentResourcesLoading
                  }
                  onClick={onClose}
                  data-selector="modal-btn-cancel"
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="button"
                  loading={!isUnassign && isBookmarkAssessmentResourcesLoading}
                  disabled={
                    loadingPreviews ||
                    isBookmarkAssessmentResourcesLoading ||
                    (!stimulusPages.length && !activityPages.length)
                  }
                  onClick={handleSubmit}
                  data-selector="modal-btn-assign"
                >
                  Assign
                </PrimaryButton>
              </Box>
            </SimpleModalActions>
          )}
        </SimpleModalContentOuter>
      ) : (
        <AssignClassDetails />
      )}
    </AssignModalWrapper>
  );
};

export default AssignAssessmentActivitiesModal;
